import superagentPromise from 'superagent-promise';
import _superagent from 'superagent';
import { notifyBackendRequestActivity } from './utils/backendActivity';

const superagent = superagentPromise(_superagent, global.Promise);

const API_ROOT = process.env.REACT_APP_API_URL;

// const encode = encodeURIComponent;
const responseBody = res => res.body;

const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка. Попробуйте позже.';

const handleError = (err) => {
  // superagent error shape
  const status = err?.status ?? err?.response?.status;
  const body = err?.response?.body;
  const text = err?.response?.text;

  const extractMessage = () => {
    if (!body && text) return text;
    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      // Common fields from various backends
      if (typeof body.message === 'string' && body.message.trim()) return body.message;
      if (typeof body.error === 'string' && body.error.trim()) return body.error;
      if (Array.isArray(body.errors) && body.errors.length) {
        const first = body.errors[0];
        if (typeof first === 'string') return first;
        if (first && typeof first.message === 'string') return first.message;
      }
      if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;
      if (typeof body.description === 'string' && body.description.trim()) return body.description;
    }
    return DEFAULT_ERROR_MESSAGE;
  };

  const msg = extractMessage();
  const isServerError = Number(status) === 500;

  // Return a friendly message instead of throwing, so UI can display it safely
  return isServerError ? DEFAULT_ERROR_MESSAGE : (msg || DEFAULT_ERROR_MESSAGE);
};

let token = null;
const tokenPlugin = req => {
  notifyBackendRequestActivity();
  if (token) {
    req.set('Authorization', `Bearer ${token}`);
  }
}

const buildQueryString = (params) =>
    Object.entries(params)
        .filter(([, v]) => v != null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");

const requests = {
  del: url =>
      superagent.del(`${API_ROOT}${url}`).use(tokenPlugin).then(responseBody),
  get: (url, options) => {
      let req = superagent.get(`${API_ROOT}${url}`).use(tokenPlugin);
      if (options && typeof options === 'object') {
        req = req.query(options);
      }
      return req.then(responseBody);
    },
  put: (url, body) =>
      superagent.put(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  post: (url, body) =>
      superagent.post(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  from: (url, body) =>
      superagent.post(`${API_ROOT}${url}`, body).use(tokenPlugin).type('form').then(res => {
        res.body = JSON.parse(res.text);
        res.body.token = res.header['authorization'];
        return responseBody(res);
      }),
  download: url =>
      superagent.get(`${API_ROOT}${url}`).use(tokenPlugin).responseType('blob'),
  patch: (url, body) =>
      superagent.patch(`${API_ROOT}${url}`, body).use(tokenPlugin).then(responseBody),
  formData: (url, body, files) => {
    const req = superagent.post(`${API_ROOT}${url}`)
    for (const [key, value] of Object.entries(body)) {
      req.field(key, value);
    }
    files.forEach((file) => req.attach(file.name, file.binary))
    return req.set('enctype', 'multipart/form-data').use(tokenPlugin).then(responseBody)
  }
};

const Auth = {
  current: () =>
      requests.get('/protected/user'),
  login: (email, password) =>
      requests.from('/login', {  username: email, password }),
  register: (username, email, password) =>
      requests.post('/users', { user: { username, email, password } }),
  save: user =>
      requests.put('/user', { user }),
  changePassword: body =>
      requests.patch(`/protected/user/password`, body)
};

const PostalService = {
  all: () =>
      requests.get(`/protected/postal/service`),
  getAllForFilter: () =>
      requests.get(`/protected/postal/service/filter`),
  get: id =>
      requests.get(`/protected/postal/service/${id}`),
  setStatus: (id, status) =>
      requests.patch(`/protected/postal/service/${id}?status=${status}`)
};


const PostalRoute = {
  all: (page, pageSize, q) =>
      requests.get(`/protected/postal/routes?page=${page}&pageSize=${pageSize}${q}`),
  get: id =>
      requests.get(`/protected/postal/routes/${id}`),
  searchCities: (name) =>
      requests.get(`/protected/direction?name=${name}`),
  addRoute: (body) => 
      requests.post('/protected/postal/routes/add', body),
  editRoute: (id, status) =>
      requests.patch(`/protected/postal/routes/${id}?status=${status}`),
  uploadFile: (body) =>
      requests.post('/protected/postal/routes/file', body),
  downloadFile: () => 
      requests.download('/protected/postal/routes/file'),
};

const Orders = {
  all: (page = 0, pageSize = 10, q) =>
      requests.get(`/protected/orders?page=${page}&pageSize=${pageSize}${q}`),
  get: id =>
      requests.get(`/protected/order/${id}`),
  getFile: (orderNum, fileType) =>
      requests.download(`/protected/file?orderNum=${orderNum}&fileType=${fileType}`),
  getChildren: id =>
      requests.get(`/protected/order/${id}/children`),
  getExcel: (q) =>
      requests.download(`/protected/report/orders/excel/map?${q}`),
  update: (orderNum, body) =>
      requests.patch(`/protected/order/${orderNum}`, body),
  updateNote: (orderNum, note) =>
      requests.patch(`/protected/order/note/${orderNum}?note=${note}`),
  create: (body, orgId) =>
      requests.post(`/protected/order?orgId=${orgId}`, body),
  getEvents: (orderNum) => 
      requests.get(`/protected/order/${orderNum}/events`),
  getExtraAddress: (orderNum) => 
      requests.get(`/protected/order/${orderNum}/extraAddress`),
  findPtcUser: (ptcUser) =>
      requests.get(`/protected/report/orders/find-ptc-user?user=${ptcUser}`)
}

const CalculatedOrder = {
  create: (body, orgId) =>
      requests.post(`/protected/calculated-route-order?orgId=${orgId}`, body)
}

const OrdersStatus = {
  all: () =>
      requests.get(`/protected/nsi/order/status`)
}

const ProductCategory = {
  all: (name='', code='', page=0, pageSize=10) =>
      requests.get(`/protected/tariff/category?page=${page}&pageSize=${pageSize}&name=${name}&code=${code}`),
  add: (body) =>
      requests.post(`/protected/tariff/category`, body),
  addList: (data, page=0, pageSize=10) =>
      requests.post(`/protected/tariff/category/file?page=${page}&pageSize=${pageSize}`, data),
}

const Offers = {
  get: (orderNum) =>
      requests.get(`/protected/offers?orderNum=${orderNum}`),
  change: (offerId, body) =>
      requests.patch(`/protected/offer/${offerId}`, body),
}

const OrderParcel = {
  get: (orderNum) =>
      requests.get(`/protected/parcels?orderNum=${orderNum}`)
}

const TaskQueue = {
  get: (orderNum) =>
      requests.get(`/protected/task_queue?orderNum=${orderNum}`),
  updateTaskQueue: (id, body) =>
      requests.patch(`/protected/task_queue/${id}`, body),
  all: () =>
      requests.get(`/protected/task_queue/type`),
  getById: (id) =>
      requests.get(`/protected/task_queue/${id}`),
  save: (body, files) =>
      requests.formData(`/protected/task_queue`, body, files)
}

const Report = {
  get: (category) =>
      requests.get(`/protected/reports/${category}`),
}

const Kato = {
  search: (name) =>
      requests.get(`/protected/nsi/kato?name=${name}`),
  searchGraph: (query, page = 0, pageSize = 10) => {
    const base = API_ROOT.replace(/\/admin$/, '');
    return superagent
      .get(`${base}/graph/kato`)
      .query({ page, pageSize, query })
      .use(tokenPlugin)
      .then(responseBody);
  }
}

const CrossDocking = {
  getRegistry: (page = 0, pageSize = 1000) =>
      requests.get('/protected/graph/cross-docking', { page, pageSize }),
  getDetail: (id) =>
      requests.get('/protected/graph/cross-docking-detail', { id }),
  getTariffGrid: (crossDockingPointIds = []) =>
      requests.get(`/protected/cross-docking-tariffs/grid`, { crossDockingPointIds }),
  createTariffRow: (body) =>
      requests.post(`/protected/cross-docking-tariffs/grid/rows`, body),
  createTariffBand: (body) =>
      requests.post(`/protected/cross-docking-tariffs/grid/bands`, body),
  patchTariffRows: (body) => {
      return requests
        .patch(`/protected/cross-docking-tariffs/grid/rows`, body)
  },
  deleteTariffRow: (crossDockingPointId, kato) => {
      return requests
        .del(`/protected/cross-docking-tariffs/grid/rows/${crossDockingPointId}/${kato}`)
  },
  importTariffPreview: (file) =>
      requests.formData(
        `/protected/cross-docking-tariffs/import/preview`,
        {},
        [{ name: "file", binary: file }]
      ),
  importTariffCommit: (body) =>
      requests.post(`/protected/cross-docking-tariffs/import/commit`, body),
  setActiveBulk: (changes) =>
      requests.patch(`/protected/graph/cross-docking/active`, { changes }),
  // Реестр точек для экрана «Точки кросс-докинга»: все типы, сроки хранения, график
  getPoints: () =>
      requests.get('/protected/graph/cross-docking/points'),
  createPoint: (body) =>
      requests.post('/protected/graph/cross-docking/points', body),
  updatePoint: (id, body) =>
      requests.put(`/protected/graph/cross-docking/points/${id}`, body),
}

const Calculator = {
  calculate: (body, orgId) =>
      requests.post(`/protected/calculator?orgId=${orgId}`, body),
}

const CalculatorShoulders = {
  calculate: (props) => {
    const base = API_ROOT.replace(/\/admin$/, '');
    return superagent
      .get(`${base}/graph/buildAdvance`)
      .query({
        from: props.from,
        to: props.to,
        weight: props.weight,
        cargoValue: props.cargoValue,
        numPack: props.numPack,
        paidWeight: props.paidWeight,
        productCategoryCode: props.productCategoryCode,
        productName: props.productName,
        deliveryFromAddress: props.deliveryFromAddress,
        deliveryToAddress: props.deliveryToAddress,
        withPrp: props.withPrp,
        withPrd: props.withPrd,
        orgId: props.orgId,
        isAsc: props.isAsc,
        sortBy: props.sortBy,
      })
      .use(tokenPlugin)
      .then(responseBody);
  },
    calculateAsync: (props, orgId) =>
        requests.post(`/protected/calculator/path?orgId=${orgId}`, {
                from: props.from,
                to: props.to,
                weight: props.weight,
                cargoValue: props.cargoValue,
                numPack: props.numPack,
                paidWeight: props.paidWeight,
                productCategoryCode: props.productCategoryCode,
                productName: props.productName,
                deliveryFromAddress: props.deliveryFromAddress,
                deliveryToAddress: props.deliveryToAddress,
                withPrp: props.withPrp,
                withPrd: props.withPrd,
                orgId: props.orgId,
                isAsc: props.isAsc,
                sortBy: props.sortBy,
        }),
    calculateAsyncByHash: (hashCodeRequest, orgId) => {
        const normalizedHash = encodeURIComponent(String(hashCodeRequest || "").replace(/^\/+/, ""));
        return requests.get(`/protected/calculator/path/${normalizedHash}`, { orgId });
    },
    calculateAdditionalAsync: (hashCodeRequest, priceTo, daysCntTo, carriers, orgId, page = 0, pageSize = 5) =>
        requests.post(
            `/protected/calculator/custom-results?orgId=${orgId}&page=${page}&pageSize=${pageSize}`,
            { hashCodeRequest, priceTo, daysCntTo, carriers },
        ),
}

const PsOrderNum = {
  add: (orderId, track) =>
    requests.post(`/protected/postal/track?orderId=${orderId}&track=${track}`, null),
  remove: (id) =>
      requests.del(`/protected/postal/track?id=${id}`),
  update: (id, track) =>
      requests.patch(`/protected/postal/track?id=${id}&track=${track}`, null)
}

const StreetAbbr = {
  all: () =>
      requests.get(`/protected/nsi/street_abbr`)
}

const Invoices = {
  get: (orderNum) =>
      requests.get(`/protected/order/${orderNum}/invoices`),
  save: (orderNum, body) =>
      requests.post(`/protected/order/${orderNum}/invoices`, body)
}

const ApiItems = {
  get: () =>
      requests.get(`/protected/payment/items`)
}

// Организации API (партнёры). all — короткий список {id, name} для выпадающих
// списков; остальное — экран «Организации». Ключ отдают только create и
// rotateApiKey, один раз: list показывает лишь, выпущен ли он.
const Organizations = {
  all: () =>
    requests.get(`/protected/orgs`),
  clients: () =>
      requests.get('/protected/nsi/client'),
  list: () =>
      requests.get('/protected/organizations'),
  create: (data) =>
      requests.post('/protected/organizations', data),
  update: (id, data) =>
      requests.put(`/protected/organizations/${id}`, data),
  rotateApiKey: (id) =>
      requests.post(`/protected/organizations/${id}/api-key`),
  block: (id) =>
      requests.post(`/protected/organizations/${id}/block`),
  unblock: (id) =>
      requests.post(`/protected/organizations/${id}/unblock`),
}

const Carriers = {
  all: (orgId) =>
      requests.get('/protected/carriers', { orgId })
}

const Lock = {
    lock: (componentName) =>
        requests.post(`/protected/ui/block`, {componentName}),
    unlock: (componentName) =>
        requests.post(`/protected/ui/unblock`, {componentName}),
}

const SlqUser = {
  all: (realmId, q, page, pageSize) =>
      requests.get(`/protected/slq_users?realmId=${realmId}&q=${q}&page=${page}&pageSize=${pageSize}`),
  get: (guid) =>
      requests.get(`/protected/slq_user/${guid}`),
  updateRequisites: (guid, body) =>
      requests.patch(`/protected/slq_user/${guid}/requisites`, body),
  getWallet: (guid) =>
      requests.get(`/protected/slq_user/${guid}/wallet`),
  getWalletHistory: (guid, page, pageSize) =>
      requests.get(`/protected/slq_user/${guid}/wallet_history?page=${page}&pageSize=${pageSize}`),
  allOptions: (q) =>
      requests.get(`/protected/slq_user/options?q=${q}`),
  getStatuses: () => 
      requests.get(`/protected/avr/status`),
  getAvrs: (data, itemsPerPage=1, page=0) => 
      requests.get(`/protected/avrs?userId=${data.userId}&itemsPerPage=${itemsPerPage}&page=${page}&status=${data.status}&avrNum=${data.avrNum}`),
  setStatus: (avrId) => 
      requests.post(`/protected/avr/status?avrId=${avrId}`),
}

const Tariff = {
  save: (data) =>
      requests.post(`/protected/tariff`, data),
  get: (userId) =>
      requests.get(`/protected/tariff/${userId}/disable`),
  disable: (userId) =>
      requests.put(`/protected/tariff/${userId}`),
  getTariff: (userId) =>
      requests.get(`/protected/tariff/${userId}`)
}

const CalcTariff = {
  getPostalServices: () =>
      requests.get('/protected/slq/calc/postalServices'),
  getUslugaPs: (psId) =>
      requests.get(`/protected/slq/calc/usluga/ps/${psId}`),
  savePsZone: (data) =>
      requests.post('/protected/slq/calc/psZone', data),
  deletePsZone: (tariffId) =>
      requests.del(`/protected/slq/calc/psZone/${tariffId}`),
  getPsZones: () =>
      requests.get('/protected/slq/calc/psZones'),
  uploadPsZoneKatoList: (tariffId, formDataWithCsvFile) =>
      requests.post(`/protected/slq/calc/upload-file/zone/${tariffId}`, formDataWithCsvFile),
  getPsZoneKatoList: (tariffId) =>
      requests.get(`/protected/slq/calc/psZone/${tariffId}/katoList`),
  getWeightList: (tariffId) =>
      requests.get(`/protected/slq/calc/psZone/${tariffId}/weightList`),
  editWeightList: (tariffId, data) =>
      requests.post(`/protected/slq/calc/psZone/${tariffId}/weight`, data),
  editZoneName: (tariffId, newName) =>
      requests.put(`/protected/slq/calc/psZone/${tariffId}/changeName?name=${newName}`, null),
  addNewKato: (tariffId, data) =>
      requests.post(`/protected/slq/calc/zoneKato/zone/${tariffId}`, data),
  deactivateKato: (tariffId, data) =>
      requests.post(`/protected/slq/calc/deactivate-zoneKato/zone/${tariffId}`, data),
  getTerminalAwayList: (code) =>
      requests.get(`/protected/slq/calc/take-away-from-terminal/?code=${code}`),
  editTerminalAwayList: (data) =>
      requests.post(`/protected/slq/calc/take-away-from-terminal`, data),
  getDeductionTTList: (code) =>
      requests.get(`/protected/slq/calc/take-away-terminals/?code=${code}`),
  editDeductionTTList: (data) =>
      requests.post(`/protected/slq/calc/take-away-terminals`, data),
}

const Terminals = {
    searchCity: (name) =>
        requests.get(`/protected/terminal/direction?name=${name}`),
    getTerminals: (id, kato, page = 0, pageSize = 10) =>
        requests.get(`/protected/terminal?id=${id}&kato=${kato}&page=${page}&pageSize=${pageSize}`),
    addTerminal: (data) => 
        requests.post(`/protected/terminal`, data),
    deleteTerminal: (id) => 
        requests.post(`/protected/terminal?id=${id}`),
    updateTerminal: (id, data) => 
        requests.post(`/protected/terminal?id=${id}`, data),
}

// Терминалы перевозчиков в opostdb (nsi.terminal): по ним api-market ставит
// адрес терминала в заказ, а граф проверяет приём и выдачу. Экран «Терминалы
// ГП/Кросс-докинг» (Terminals) правит другую копию — slqdb через calc-service,
// между собой копии не синхронизируются. Контракт тот же, что у Terminals.
const NsiTerminals = {
    searchCity: (name) =>
        requests.get(`/protected/nsi/terminal/direction?name=${encodeURIComponent(name || '')}`),
    getTerminals: (id, kato, page = 0, pageSize = 10) =>
        requests.get(`/protected/nsi/terminal?id=${id}&kato=${kato}&page=${page}&pageSize=${pageSize}`),
    addTerminal: (data) =>
        requests.post(`/protected/nsi/terminal`, data),
    deleteTerminal: (id) =>
        requests.post(`/protected/nsi/terminal?id=${id}`),
    updateTerminal: (id, data) =>
        requests.post(`/protected/nsi/terminal?id=${id}`, data),
}

const Reports = {
    getStatus: () => 
        requests.get('/protected/report/async/orders/excel/map'),
    getProduceStatus: () => 
        requests.get('/protected/report/async/orders/excel/pending'),
    createReport: (params) => 
        requests.post(`/protected/report/async/orders/excel/map?${params}`),
    downloadReport: () => 
        requests.download('/protected/report/async/orders/excel/file'),
}

const StatusesMonitors = {
    getTimelineStatusses: () =>
        requests.get('/protected/timeline/status'),
    getAdminStatusses: () =>
        requests.get('/protected/timeline/status_admin'),
    getOrdersList: ({ status, statusAdmin, ps, orderNum }, page, pageSize) =>
        requests.get('/protected/timeline', {
            page,
            pageSize,
            status,
            statusAdmin,
            ps,
            orderNum
        }),
    getOrderHistory: (orderNum, page, pageSize) =>
        requests.get(`/protected/timeline/${orderNum}`, {
          page,
          pageSize
        }),
    setTimelineOrder: (body) =>
        requests.post('/protected/timeline', body),
    getTimelineByOrder: (orderNum) =>
        requests.get(`/protected/order/${orderNum}/timeline`)
}
const CatalogType = {
    listCatalogTypes: (code, page = 0, pageSize = 10) =>
        requests.get(`/protected/catalogType/${code}?page=${page}&pageSize=${pageSize}`),
    searchCatalogTypes: (code, name, page = 0, pageSize = 10) =>
        requests.get(`/protected/catalogType/${code}/search?name=${name}&page=${page}&pageSize=${pageSize}`),
    addCatalogType: (code, body) =>
        requests.post(`/protected/catalogType/${code}`, body),
    updateCatalogType: (code, id, body) =>
        requests.put(`/protected/catalogType/${code}?id=${id}`, body),
    deleteCatalogType: (code, id) =>
        requests.del(`/protected/catalogType/${code}?id=${id}`)
}

const AccountingDictionary = {
    listRecords: (code, page = 0, pageSize = 10) =>
        requests.get(`/protected/dictionary/${code}/page?page=${page}&pageSize=${pageSize}`),
    searchRecords: (code, params, page = 0, pageSize = 10) => {
        const query = buildQueryString({ ...params, page, pageSize });
        return requests.get(`/protected/dictionary/${code}/search?${query}`);
    },
    getById: (code, id) => requests.get(`/protected/dictionary/${code}/${id}`),
    addRecord: (code, upsertDto) =>
        requests.post(`/protected/dictionary/${code}`, upsertDto),
    updateRecord: (code, id, upsertDto) =>
        requests.put(`/protected/dictionary/${code}/${id}`, upsertDto),
    deleteRecord: (code, id) =>
        requests.del(`/protected/dictionary/${code}/${id}`)
}

const SQLPostalService = {
    getPostalServices: (page = 0, size = 10) =>
        requests.get('/protected/api/v1/ps-tariffs', { page, size }),
    getPostalService: (id) =>
        requests.get(`/protected/api/v1/ps-tariffs/${id}`),
    flagsPostalService: (id, data) =>
        requests.patch(`/protected/api/v1/ps-tariffs/${id}/flags`, data),
}

const Dashboard = {
    getCounts: () =>
        requests.get('/protected/admin-dashboard/counts'),
    getFailedTasks: (page = 0, size = 20) =>
        requests.get('/protected/admin-dashboard/fails', { page, size }),
    getNewOverdue: (page = 0, size = 20) =>
        requests.get('/protected/admin-dashboard/new/overdue', { page, size }),
    getAverageProcessingTime: () =>
        requests.get('/protected/admin-dashboard/averageProcessingSecondsByType'),
    getRabbitQueue: () =>
        requests.get('/protected/admin-dashboard/rabbit/queues'),
    syncOrderCount: () =>
        requests.get('/protected/admin-dashboard/order/count-sync'),
}

const DpdTimeout = {
    getServiceTimeouts: () =>
        requests.get('/protected/dpd/timeout'),
    updateServiceTimeout: (service, data) =>
        requests.patch(`/protected/dpd/timeout/${service}`, data),
    refreshServiceTimeouts: (server) =>
        requests.post(`/protected/dpd/timeout/refresh?server=${server}`),
}

const Users = {
    getUsers: (page = 0, size = 20, filters = {}) =>
        requests.get('/protected/users', { page, size, ...filters }),
    getUser: (id) =>
        requests.get(`/protected/users/${id}`),
    updateUser: (id, data) =>
        requests.patch(`/protected/users/${id}`, data),
    createUser: (data) =>
        requests.post('/protected/users', data),
    lock: (id) =>
        requests.patch(`/protected/users/${id}/block`),
    unlock: (id) =>
        requests.patch(`/protected/users/${id}/activate`),
    resetPassword: (id) =>
        requests.post(`/protected/users/${id}/password/reset`, {}),
}
const Public = {
    resetPassword: (data) =>
        requests.post('/public/password/reset', data),
}

const AuditLogs = {
    getAuditLogs: (page = 0, size = 20, filters = {}) =>
        requests.get('/protected/slq/logs', { page, size, ...filters }),
}

const SlqTariffs = {
    list: ({ page = 0, size = 20, active, platform, postalType, clientName } = {}) =>
        requests.get('/protected/slq-tariffs', { page, size, active, platform, postalType, clientName }),
    get: (id) =>
        requests.get(`/protected/slq-tariffs/${id}`),
    create: (data) =>
        requests.post('/protected/slq-tariffs', data),
    update: (id, data) =>
        requests.put(`/protected/slq-tariffs/${id}`, data),
    setActive: (id, active) =>
        requests.patch(`/protected/slq-tariffs/${id}/active?active=${active}`),
    remove: (id) =>
        requests.del(`/protected/slq-tariffs/${id}`),
}

// Наценки нового калькулятора (pricing.org_tariffs в api-calculator).
// marginPrice = null -> наценка не применяется, для PTC цена идёт по себестоимости.
const OrgTariffs = {
    list: ({ page = 0, size = 20, active, platform, clientName, psCode, psTypeCode } = {}) =>
        requests.get('/protected/org-tariffs', { page, size, active, platform, clientName, psCode, psTypeCode }),
    // справочник пар «перевозчик — услуга» из nsi.postal_services
    postalServices: () =>
        requests.get('/protected/org-tariffs/postal-services'),
    get: (id) =>
        requests.get(`/protected/org-tariffs/${id}`),
    create: (data) =>
        requests.post('/protected/org-tariffs', data),
    update: (id, data) =>
        requests.put(`/protected/org-tariffs/${id}`, data),
    setActive: (id, active) =>
        requests.patch(`/protected/org-tariffs/${id}/active?active=${active}`),
    remove: (id) =>
        requests.del(`/protected/org-tariffs/${id}`),
}

// Скидки и тарифные карты партнёров — строка на плечо доставки
// (FIRST / MIDDLE / LAST / INTRACITY).
const PartnerDiscounts = {
    list: () =>
        requests.get('/protected/partner-discounts'),
    // создаёт правило или перезаписывает существующее — по паре «организация + плечо»
    save: (data) =>
        requests.post('/protected/partner-discounts', data),
    // тумблер переключает организацию целиком: полпрофиля со скидкой означало бы
    // полмаршрута со скидкой
    setActive: (orgId, active) =>
        requests.patch(`/protected/partner-discounts/${orgId}/active?active=${active}`),
    // очистка плеча — это удаление строки: правило без процента и без карты
    // ничего не делает, а место в списке занимает
    remove: (orgId, mileType) =>
        requests.del(`/protected/partner-discounts/${orgId}/${mileType}`),

    // Те же правила, но для юрлица slq.kz. Список общий — клиентские строки
    // приходят из той же ручки и отличаются subjectType.
    saveClient: (data) =>
        requests.post('/protected/partner-discounts', { ...data, subjectType: 'CLIENT' }),
    removeClient: (clientGuid, mileType) =>
        requests.del(`/protected/partner-discounts/clients/${clientGuid}/${mileType}`),
    setClientActive: (clientGuid, active) =>
        requests.patch(`/protected/partner-discounts/clients/${clientGuid}/active?active=${active}`),
}

// Именованные тарифные карты: тариф на одно плечо, который действует только для
// назначенных партнёров. Содержимое лежит в тех же таблицах, что и стандартный
// тариф, и отличается только принадлежностью карте, поэтому заливка идёт тем же
// файлом и тем же разбором — но ЗАМЕЩАЕТ содержимое именно этой карты.
const TariffPlans = {
    list: ({ mileType, isActive, query } = {}) => {
        const params = new URLSearchParams();
        if (mileType) params.append("mileType", mileType);
        if (isActive != null) params.append("isActive", isActive);
        if (query) params.append("query", query);
        const suffix = params.toString();
        return requests.get(`/protected/tariff-plans${suffix ? `?${suffix}` : ""}`);
    },
    get: (id) =>
        requests.get(`/protected/tariff-plans/${id}`),
    create: (data) =>
        requests.post('/protected/tariff-plans', data),
    update: (id, data) =>
        requests.put(`/protected/tariff-plans/${id}`, data),
    deletionPreview: (id) =>
        requests.get(`/protected/tariff-plans/${id}/deletion-preview`),
    remove: (id) =>
        requests.del(`/protected/tariff-plans/${id}`),
    importPreview: (file, intracityOnly = false) =>
        requests.formData(
          `/protected/tariff-plans/import/preview?intracityOnly=${intracityOnly}`,
          {},
          [{ name: "file", binary: file }]
        ),
    importCommit: ({ planId, file, psId, intracityOnly = false, fingerprint, probeWeights }) => {
        const query = new URLSearchParams({ psId, intracityOnly });
        if (fingerprint) query.append("fingerprint", fingerprint);
        (probeWeights || []).forEach((w) => query.append("probeWeights", w));
        return requests.formData(
          `/protected/tariff-plans/${planId}/import/commit?${query.toString()}`,
          {},
          [{ name: "file", binary: file }]
        );
    },
}

// Настраиваемые параметры приложения (nsi.app_settings).
// Сам список закрыт: параметр заводится миграцией вместе с кодом, который его
// читает, поэтому ручек на создание и удаление нет — правится только значение.
// Правка уходит пачкой: на экране одна кнопка на всю таблицу.
const AppSettings = {
    list: () =>
        requests.get('/protected/app-settings'),
    update: (items) =>
        requests.put('/protected/app-settings', items),
}

// Областные центры Казпочты (nsi.kazpost_regional_centers). api-order ставит
// отметку 150 отправлению P154, если оба пункта в этом списке, иначе 151.
// Добавление по коду КАТО — его отдаёт Kato.search.
const KazpostRegionalCenters = {
    list: () =>
        requests.get('/protected/nsi/kazpost/regional-centers'),
    add: (kato) =>
        requests.post('/protected/nsi/kazpost/regional-centers', { kato }),
    remove: (id) =>
        requests.del(`/protected/nsi/kazpost/regional-centers/${id}`),
}

// Закрытые направления. Блокировки карты приезжают заливкой файла (колонка
// «блокировать направление»), глобальные и партнёрские заводит оператор.
// Закрытый вектор не откатывается на стандартный расчёт — расчёта не будет вовсе.
const BlockedDirections = {
    list: ({ scope, tariffPlanId, isActive } = {}) => {
        const params = new URLSearchParams();
        if (scope) params.append("scope", scope);
        if (tariffPlanId) params.append("tariffPlanId", tariffPlanId);
        if (isActive != null) params.append("isActive", isActive);
        const suffix = params.toString();
        return requests.get(`/protected/blocked-directions${suffix ? `?${suffix}` : ""}`);
    },
    create: (data) =>
        requests.post('/protected/blocked-directions', data),
    update: (id, data) =>
        requests.put(`/protected/blocked-directions/${id}`, data),
    remove: (id) =>
        requests.del(`/protected/blocked-directions/${id}`),
}

// Заливка тарифов перевозчика из файла (api-calculator через slq-backend).
// Два шага: предпросмотр показывает содержимое файла, коммит записывает.
// Файл на коммит уходит заново — так на сервере не остаётся состояния между
// шагами, а отпечаток из предпросмотра не даёт залить не тот файл, который
// смотрели. Заливка ЗАМЕЩАЕТ тариф пары «перевозчик — услуга» целиком.
const CarrierTariffs = {
    // psId — услуга, в которую собираются залить: по ней предпросмотр
    // показывает, что заливка удалит и что оставит.
    importPreview: (file, intracityOnly = false, psId) => {
        const query = new URLSearchParams({ intracityOnly });
        if (psId) query.append("psId", psId);
        return requests.formData(
          `/protected/carrier-tariffs/import/preview?${query.toString()}`,
          {},
          [{ name: "file", binary: file }]
        );
    },
    importCommit: ({ file, psId, intracityOnly = false, fingerprint, probeWeights }) => {
        const query = new URLSearchParams({ psId, intracityOnly });
        if (fingerprint) query.append("fingerprint", fingerprint);
        (probeWeights || []).forEach((w) => query.append("probeWeights", w));
        return requests.formData(
          `/protected/carrier-tariffs/import/commit?${query.toString()}`,
          {},
          [{ name: "file", binary: file }]
        );
    },
}

// Администрирование тарифов нового калькулятора: ноги перевозчика и их
// весовые лестницы (pricing.carrier_delivery_legs и tariff_weight_ranges).
//
// Лестница ходит точками, а не диапазонами: нижняя граница диапазона
// производная, задать её снаружи нельзя. Правка — только заменой целиком.
const CarrierDeliveryLegs = {
    search: ({ psId, mileTypes, originQuery, destinationQuery, page = 0, size = 20 } = {}) => {
        const query = new URLSearchParams({ page, size });
        if (psId) query.append("psId", psId);
        (mileTypes || []).forEach((m) => query.append("mileTypes", m));
        if (originQuery) query.append("originQuery", originQuery);
        if (destinationQuery) query.append("destinationQuery", destinationQuery);
        return requests.get(`/protected/carrier-delivery-legs?${query.toString()}`);
    },
    get: (legId) =>
        requests.get(`/protected/carrier-delivery-legs/${legId}`),
    deletionPreview: (legId) =>
        requests.get(`/protected/carrier-delivery-legs/${legId}/deletion-preview`),
    remove: (legId) =>
        requests.del(`/protected/carrier-delivery-legs/${legId}`),
    readLadder: (legId) =>
        requests.get(`/protected/carrier-delivery-legs/${legId}/ladder`),
    // Показывает цены, ничего не сохраняя.
    previewLadder: (legId, body) =>
        requests.post(`/protected/carrier-delivery-legs/${legId}/ladder/preview`, body),
    replaceLadder: (legId, body) =>
        requests.put(`/protected/carrier-delivery-legs/${legId}/ladder`, body),
}

const Payments = {
    getAllPayments: ({ q, transactionId, returned, startDate, endDate,  itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/payments/all', {
            q,
            transactionId,
            returned,
            startDate,
            endDate,
            itemsPerPage,
            page,
        }),
    // Download payment report as a file (blob) with headers preserved for filename extraction
    getPaymentReportFile: ({ q, startDate, endDate, transactionId } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/payments/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, transactionId });
        return req.responseType('blob'); // returns response, not just body
    },
    getAllAvrs: ({ q, startDate, endDate, statusId, avrNum, itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/avrs/all', {
            q,
            startDate,
            endDate,
            status: statusId,
            avrNum,
            itemsPerPage,
            page,
        }),
    getAllPtcAvrs: ({ q, startDate, endDate, status, avrNum, itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/ptc/avrs/all', {
            q,
            startDate,
            endDate,
            status,
            avrNum,
            itemsPerPage,
            page,
        }),
    // Download AVR report as a file (blob); supports filtering by statusId and avrNum
    getAvrReportFile: ({ q, startDate, endDate, statusId, avrNum } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/avrs/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, statusId, avrNum });
        return req.responseType('blob');
    },
    getPtcAvrReportFile: ({ q, startDate, endDate, statusId, avrNum } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/ptc/avrs/report`).use(tokenPlugin);
        req = req.query({ q, startDate, endDate, statusId, avrNum });
        return req.responseType('blob');
    },
    // Отчет по АВР чекам: проекция состояния фискализации из ptc-fiscal-service
    getAllAvrChecks: ({ slqNum, checkNumber, fio, iin, signedFrom, signedTo, status, itemsPerPage = 25, page = 0 } = {}) =>
        requests.get('/protected/avrs/checks/all', {
            slqNum,
            checkNumber,
            fio,
            iin,
            signedFrom,
            signedTo,
            status,
            itemsPerPage,
            page,
        }),
    // Отзыв фискального чека. Проверки применимости (окно отката, статус, повторный запрос)
    // делает ptc-fiscal-service и объясняет отказ текстом — здесь его только показываем.
    rollbackAvrCheck: (avrId) =>
        requests.post(`/protected/avrs/checks/${avrId}/rollback`, {}),
    getAvrCheckReportFile: ({ slqNum, checkNumber, fio, iin, signedFrom, signedTo, status } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/avrs/checks/report`).use(tokenPlugin);
        req = req.query({ slqNum, checkNumber, fio, iin, signedFrom, signedTo, status });
        return req.responseType('blob');
    },
    getJurAvrPdf: (avrId, userId) =>
        superagent.get(`${API_ROOT}/protected/avr/${avrId}/pdf`).query({ userId }).set('Accept', 'application/pdf').use(tokenPlugin).responseType('blob'),

    getPtcWithdrawsReportFile: ({ paymentId, iin, name, withdrawDateFrom, withdrawDateTo, amount, feeAmount, phone, email, contractNumber, contractDateFrom, contractDateTo, operationId, currency } = {}) => {
        let req = superagent.get(`${API_ROOT}/protected/ptc/withdraws/report`).use(tokenPlugin);
        req = req.query({
            paymentId,
            iin,
            name,
            withdrawDateFrom,
            withdrawDateTo,
            amount,
            feeAmount,
            phone,
            email,
            contractNumber,
            contractDateFrom,
            contractDateTo,
            operationId,
            currency
        });
        return req.responseType('blob');
    },
    getAllWithdraws: ({ paymentId, iin, name, withdrawDateFrom, withdrawDateTo, amount, feeAmount, phone, email, contractNumber, contractDateFrom, contractDateTo, operationId, currency, page = 0, itemsPerPage = 25 } = {}) =>
        requests.get('/protected/ptc/withdraws/all', {
            paymentId,
            iin,
            name,
            withdrawDateFrom,
            withdrawDateTo,
            amount,
            feeAmount,
            phone,
            email,
            contractNumber,
            contractDateFrom,
            contractDateTo,
            operationId,
            currency,
            page,
            itemsPerPage,
        }),
}

const Oktru = {
    getPage: (filters = {}, page = 0, size = 10) =>
        requests.get('/nsi/oktru/page', { ...filters, page, size }),
    getById: (id) =>
        requests.get(`/nsi/oktru/${id}`),
    create: (body) =>
        requests.post('/nsi/oktru/create', body),
    update: (body) =>
        requests.put('/nsi/oktru/update', body),
    softDelete: (id) =>
        requests.del(`/nsi/oktru/${id}/soft-delete`),
    restore: (id) =>
        requests.patch(`/nsi/oktru/${id}/restore`, null),
    importCsv: (file) => {
        const req = superagent.post(`${API_ROOT}/nsi/oktru/import-csv`);
        req.attach('file', file);
        return req.use(tokenPlugin).then(responseBody);
    },
}

const AgentReward = {
    getPage: (params) =>
        requests.get('/protected/agent-reward', params),
    getExcel: (params) =>
        requests.download(`/protected/agent-reward/excel?${buildQueryString(params)}`),
};

const ObligationsJournal = {
    list: (page, size) =>
        requests.get(`/api/billing/obligations?page=${page}&size=${size}`),
    search: (filters, page, size) =>
        requests.post(`/api/billing/obligations/search?page=${page}&size=${size}`, filters),
    details: (obligationId) =>
        requests.get(`/api/billing/obligations/${obligationId}`)
};

export default {
  Reports,
  Auth,
  PostalService,
  PostalRoute,
  Orders,
  OrdersStatus,
  ProductCategory,
  Offers,
  OrderParcel,
  TaskQueue,
  Report,
  Kato,
  Calculator,
  CalculatorShoulders,
  CalculatedOrder,
  PsOrderNum,
  StreetAbbr,
  Invoices,
  ApiItems,
  Organizations,
  Carriers,
  Lock,
  SlqUser,
  Tariff,
  Terminals,
  NsiTerminals,
  CalcTariff,
  StatusesMonitors,
  SQLPostalService,
  Dashboard,
  DpdTimeout,
  Users,
  Public,
  AuditLogs,
  SlqTariffs,
  OrgTariffs,
  PartnerDiscounts,
  TariffPlans,
  AppSettings,
  KazpostRegionalCenters,
  BlockedDirections,
  CarrierTariffs,
  CarrierDeliveryLegs,
  Payments,
  CatalogType,
  AccountingDictionary,
  ObligationsJournal,
  Oktru,
  AgentReward,
  CrossDocking,
  setToken: _token => { token = _token; },
  handleError
};
